import Edit from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { GeofenceType } from '@treadinc/horizon-api-spec';
import { forwardRef, useCallback } from 'react';

import { Site } from '~hooks/useSites/models';

/**
 * Helper function to get the display label for a site's geofence type.
 * Returns 'Equipment' for moving sites, or the geofence type (Circle/Polygon) otherwise.
 */
function getGeofenceTypeLabel(site: Site): string | null {
  const nextBillionGeofence = site.nextBillionGeofence;
  if (!nextBillionGeofence) return null;

  // Check if it's a moving/equipment site
  if (site.movingSite || nextBillionGeofence.movingGeofence) {
    return 'Equipment';
  }

  // Get the geofence type
  const type = nextBillionGeofence.geofenceType;
  if (type === GeofenceType.CIRCLE) return 'Circle';
  if (type === GeofenceType.POLYGON) return 'Polygon';

  return null;
}

interface SiteCardProps {
  site: Site;
  isSelected: boolean;
  onClick: (siteId: string) => void;
  onEditClick: (siteId: string) => void;
}

export const SiteCard = forwardRef<HTMLDivElement, SiteCardProps>(
  ({ site, isSelected, onClick, onEditClick }, ref) => {
    const theme = useTheme();

    const handleClick = useCallback(() => {
      onClick(site.id);
    }, [onClick, site.id]);

    const handleEditClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onEditClick(site.id);
      },
      [onEditClick, site.id],
    );

    const geofenceType = getGeofenceTypeLabel(site);

    return (
      <Box
        ref={ref}
        onClick={handleClick}
        sx={{
          bgcolor: theme.semantic.surfaceRaised,
          borderRadius: theme.brandV2.borderRadius,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderLeft: `2px solid ${isSelected ? theme.palette.info.main : theme.brandV2.colors.treadGray7}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          p: 1,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.brandV2.colors.treadGray10,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: theme.brandV2.colors.treadBlack,
              wordBreak: 'break-word',
            }}
          >
            {site.name}
          </Typography>
          <IconButton
            size="small"
            onClick={handleEditClick}
            sx={{
              ml: 0.5,
              mt: -0.5,
              flexShrink: 0,
              '&:hover': { backgroundColor: theme.brandV2.colors.treadGray8 },
            }}
          >
            <Edit sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        {site.fullAddress && (
          <Typography
            sx={{
              fontSize: '12px',
              color: theme.brandV2.colors.treadGray3,
              wordBreak: 'break-word',
            }}
          >
            {site.fullAddress}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mt: 0.5,
          }}
        >
          {site.siteType && (
            <Chip
              size="small"
              label={site.siteType}
              sx={{
                height: '20px',
                fontSize: '11px',
                bgcolor: theme.brandV2.colors.treadGray9,
                color: theme.brandV2.colors.treadBlack,
              }}
            />
          )}
          {geofenceType && (
            <Chip
              size="small"
              label={geofenceType}
              sx={{
                height: '20px',
                fontSize: '11px',
                bgcolor: theme.brandV2.colors.treadGray9,
                color: theme.brandV2.colors.treadBlack,
              }}
            />
          )}
          {site.companyName && (
            <Typography
              sx={{
                fontSize: '11px',
                color: theme.brandV2.colors.treadGray4,
              }}
            >
              {site.companyName}
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);

SiteCard.displayName = 'SiteCard';
